import React from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { getAbsoluteUrl } from "../../utils/metaUtils";

interface ProductQRCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: string;
  productTitle: string;
}

export const ProductQRCodeModal: React.FC<ProductQRCodeModalProps> = ({
  isOpen,
  onClose,
  productId,
  productTitle,
}) => {
  // Generate product URL using product ID
  const productUrl = getAbsoluteUrl(`/products/${productId}`);
  
  // Dynamically import QRCode component to handle cases where package might not be installed
  const [QRCode, setQRCode] = React.useState<any>(null);
  const [qrError, setQrError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      // Try to import react-qr-code
      import("react-qr-code")
        .then((module) => {
          // react-qr-code exports QRCode as default export
          const QRCodeComponent = module.default;
          if (QRCodeComponent) {
            setQRCode(() => QRCodeComponent);
            setQrError(null);
          } else {
            setQrError("QR code library not available. Please install react-qr-code package.");
            setQRCode(null);
          }
        })
        .catch(() => {
          setQrError("QR code library not available. Please install react-qr-code package.");
          setQRCode(null);
        });
    }
  }, [isOpen]);

  const handleDownload = () => {
    // Find the SVG element in the rendered QR code
    const qrContainer = document.querySelector('[data-qr-code]');
    const svg = qrContainer?.querySelector("svg");
    
    if (!svg) return;

    try {
      // Serialize SVG to string
      const svgData = new XMLSerializer().serializeToString(svg);
      
      // Create blob and download
      const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);
      
      const link = document.createElement("a");
      link.href = url;
      link.download = `qr-code-${productTitle.replace(/[^a-z0-9]/gi, "-").toLowerCase()}.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download QR code:", error);
      alert("Failed to download QR code. Please try right-clicking on the QR code and saving it manually.");
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(productUrl).then(() => {
      // Show success feedback (you might want to use a toast here)
      alert("Product URL copied to clipboard!");
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Product QR Code" size="md">
      <div className="space-y-4">
        <div className="text-sm text-gray-600">
          <p className="font-medium mb-1">{productTitle}</p>
          <p className="text-xs text-gray-500 break-all">{productUrl}</p>
        </div>

        {qrError ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">{qrError}</p>
            <p className="text-xs text-yellow-700 mt-2">
              Run: <code className="bg-yellow-100 px-1 rounded">npm install react-qr-code</code>
            </p>
          </div>
        ) : QRCode ? (
          <div className="flex flex-col items-center space-y-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200" data-qr-code>
              <QRCode
                value={productUrl}
                size={256}
                level="H"
                bgColor="#FFFFFF"
                fgColor="#000000"
              />
            </div>
            <div className="flex gap-2 w-full">
              <Button variant="outline" onClick={handleCopyUrl} className="flex-1">
                Copy URL
              </Button>
              <Button variant="primary" onClick={handleDownload} className="flex-1">
                Download QR Code
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-400">Loading QR code...</div>
          </div>
        )}
      </div>
    </Modal>
  );
};
